import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { SidebarSearchDropdown } from './SidebarSearchDropdown';
import SharedButton from '@/global/Button/Button';
import { DATASETS, getName, getTitle } from '../../../../../../components/Constants';
import '../../../SearchResults.css';
import { trackEvent } from '@/utils/analytics';

interface SearchItemsPanelContentProps {
  contextSetSearchItems?: ((items: any[]) => void) | undefined;
  searchInputValue: string;
  setSearchInputValue: (value: string) => void;
  pendingItems: any[];
  setPendingItems: (updater: (prev: any[]) => any[]) => void;
  setSidebarDropdownOpen: (open: boolean) => void;
  activeSearchDataset?: string;
  searchItems: any[];
  isMapping: boolean;
  onOpenSearchItemInfo?: (item: any) => void;
  onRemoveSearchItem?: (index: number) => void;
  onClearAllSearchItems?: () => void;
  searchSuggestions: string[];
  searchPlaceholder: string;
}

export function SearchItemsPanelContent({
  contextSetSearchItems,
  searchInputValue,
  setSearchInputValue,
  pendingItems,
  setPendingItems,
  setSidebarDropdownOpen,
  activeSearchDataset,
  searchItems,
  isMapping,
  onOpenSearchItemInfo,
  onRemoveSearchItem,
  onClearAllSearchItems,
  searchSuggestions,
  searchPlaceholder,
}: SearchItemsPanelContentProps) {
  return (
    <div className="space-y-3 py-2 px-2 min-w-0">
      {contextSetSearchItems && (
        <div className="min-w-0">
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
              <MagnifyingGlassIcon className="h-4 w-4 text-[#7D86A3]" />
            </span>
            <input
              type="text"
              autoComplete="off"
              placeholder={searchPlaceholder}
              value={searchInputValue}
              onChange={e => setSearchInputValue(e.target.value)}
              data-demo-tour="add-search-item"
              className="w-full min-w-0 rounded-xl border border-[#CBC8DA] bg-[#E8E7F2] py-2 pl-9 pr-3 text-sm text-[#4C536C] placeholder:text-[#9AA3BC] focus:border-[#B8B2CF] focus:ring-2 focus:ring-[#E6E2F4]"
            />
          </div>
          {searchInputValue.trim().length >= 3 && (
            <div className="mt-2 relative z-50 min-w-0">
              <SidebarSearchDropdown
                value={searchInputValue}
                setValue={setSearchInputValue}
                searchItems={pendingItems}
                setSearchItems={setPendingItems}
                onDropdownToggle={setSidebarDropdownOpen}
                allowedDataset={activeSearchDataset}
              />
            </div>
          )}
        </div>
      )}
      {searchItems.length > 0 && (
        <>
          <div className="flex max-h-48 min-h-0 flex-wrap items-center gap-2 overflow-y-auto overscroll-contain min-w-0">
            {searchItems.map((item, index) => {
              const title = getTitle(item);
              const id = getName(item);
              const titleStr =
                typeof title === 'object' && title !== null && 'title' in title
                  ? String((title as { title?: string }).title ?? id)
                  : String(title ?? '');
              const showActions =
                (!isMapping || !!onOpenSearchItemInfo || !!onRemoveSearchItem) &&
                (typeof title !== 'object' || (title as { title?: string })?.title !== DATASETS.ALL);
              const canOpenInfo = showActions && !!onOpenSearchItemInfo;
              return (
                <span
                  key={`${String(id ?? '')}-${index}`}
                  className={`inline-flex max-w-full items-center rounded-full border tag-panel bg-[#FFE6F3] px-2.5 py-1 text-xs font-medium text-pink ${
                    canOpenInfo ? 'search-items-tag' : ''
                  }`}
                  onClick={() => {
                    if (canOpenInfo) {
                      trackEvent({
                        name: 'open_item_info',
                        buttonId: 'search_items_panel_open_info',
                        label: 'Open search item info',
                        page: 'search_results',
                        section: 'search_items_panel',
                        feature: 'search_items',
                        eventGroup: 'search',
                        eventCategory: 'search',
                      });
                      onOpenSearchItemInfo(item);
                    }
                  }}
                  onKeyDown={event => {
                    if (!canOpenInfo) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onOpenSearchItemInfo(item);
                    }
                  }}
                  role={canOpenInfo ? 'button' : undefined}
                  tabIndex={canOpenInfo ? 0 : undefined}
                  aria-label={canOpenInfo ? `Open info for ${titleStr}` : undefined}
                >
                  <span className="search-items-tag-label truncate">{titleStr}</span>
                  {showActions && onRemoveSearchItem && (
                    <>
                      <span aria-hidden="true" className="search-items-tag-divider mx-1" />
                      <button
                        type="button"
                        onClick={event => {
                          // Prevent opening info when removing a search item.
                          event.stopPropagation();
                          trackEvent({
                            name: 'remove_search_item',
                            buttonId: 'search_items_panel_remove_item',
                            label: 'Remove search item',
                            page: 'search_results',
                            section: 'search_items_panel',
                            feature: 'search_items',
                            eventGroup: 'search',
                            eventCategory: 'search',
                          });
                          if (searchItems.length === 1 && onClearAllSearchItems) {
                            onClearAllSearchItems();
                          } else {
                            onRemoveSearchItem(index);
                          }
                        }}
                        className="search-items-tag-close rounded-full p-0.5"
                        title="Remove from search"
                        data-testid="close-info-button"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </span>
              );
            })}
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            {onClearAllSearchItems && (
              <SharedButton
                type="button"
                variant="secondaryText"
                size="xs"
                onClick={() => {
                  trackEvent({
                    name: 'clear_all_search_items',
                    buttonId: 'search_items_panel_clear_all',
                    label: 'Clear all search items',
                    page: 'search_results',
                    section: 'search_items_panel',
                    feature: 'search_items',
                    eventGroup: 'search',
                    eventCategory: 'search',
                  });
                  onClearAllSearchItems();
                }}
              >
                Clear all
              </SharedButton>
            )}
          </div>
        </>
      )}
      {contextSetSearchItems && (
        <div className="min-w-0">
          <p className="mb-2 mt-3 ml-1 text-xs uppercase tracking-widest text-gray-400">Suggestions</p>
          <div className="flex flex-wrap gap-2">
            {searchSuggestions.map(suggestion => (
              <button
                key={suggestion}
                type="button"
                onClick={() => setSearchInputValue(suggestion)}
                className="rounded-full px-2.5 py-1 text-xs text-gray-400 hover:text-gray-600 hover:bg-[#EEF1F7]"
                style={{ border: '1px dashed #C9CFDD' }}
              >
                + {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
